import React, { useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@components/buttons";
import { MostPlayTicketActionsTypeEnum } from "@data-types/analytics-tracker/QuickPlayInteractionTypeEnum";
import { useRootAppStore } from "@hooks";
import { useFixBody } from "@state/hooks/shared";
import { ChangePageButton } from "@state/quick-play-store/MostPlayedTicketsStore";
import { getCurrentCulture } from "@utils";
import { analyticsTracker } from "@utils/analytics";
import classNames from "classnames";
import { observer } from "mobx-react";

import { QuickPlayPaginationButton } from "../common/QuickPlayPaginationButton";
import { MostPlayedTicketsPopupBody } from "./components/MostPlayedTicketsPopupBody";

export default observer(function MostPlayTicketsPopup() {
	const { t } = useTranslation("mybets");

	const rootStore = useRootAppStore();

	const popupElement = useRef<null | HTMLDivElement>(null);
	const location = useLocation();

	useFixBody(
		true,
		rootStore.quickPlayStore.mostPlayedTicketsStore.isPopupDisplayed
	);

	if (
		!location.pathname.includes("home") &&
		!location.pathname.includes("most-played-tickets")
	) {
		rootStore.quickPlayStore.mostPlayedTicketsStore.deactivatePopup();
	}
	useEffect(() => {
		analyticsTracker.trackModalInteraction("most-played");
	}, []);

	useEffect(() => {
		popupElement.current?.scrollTo({ top: 0 });
	}, [rootStore.quickPlayStore.mostPlayedTicketsStore.selectedBetSlip]);
	const isOnQuickPlayPage = location.pathname.includes("quick-play");

	const isPrevAndNextDisabled =
		rootStore.quickPlayStore.mostPlayedTicketsStore.currentActiveList
			.size == 1;
	if (!rootStore.quickPlayStore.mostPlayedTicketsStore.selectedBetSlip) {
		return null;
	}

	return (
		<div className="popup popup--full">
			<div className="popup__card popup--most__card">
				<div className="popup__header">
					<div className="popup__title">
						<i className="u-icon u-icon--base u-icon--top--str u-color--rate--low u-mar--right--xtny" />
						<div className="popup__title__inner">
							{t("QUICK_PLAY.MOST_PLAYED_TICKETS.TICKET")} #
							{
								rootStore.quickPlayStore.mostPlayedTicketsStore
									.betSlipIndex
							}
						</div>
					</div>
					<button
						type="button"
						className="popup__close btn btn--icon btn--square btn--square--xsml btn--primary--inverted"
						onClick={
							rootStore.quickPlayStore.mostPlayedTicketsStore
								.deactivatePopup
						}
					>
						<i className="u-icon u-icon--tny u-icon--close" />
					</button>
				</div>
				<div ref={popupElement} className="popup__body">
					<MostPlayedTicketsPopupBody
						key={
							rootStore.quickPlayStore.mostPlayedTicketsStore
								.selectedBetSlip.betSlipId
						}
						betSlip={
							rootStore.quickPlayStore.mostPlayedTicketsStore
								.selectedBetSlip
						}
						displayAdditionalInfo
					/>
				</div>
				<div className="popup__footer popup__footer--bordered">
					{!isOnQuickPlayPage && (
						<div className="popup__footer__left">
							<Link
								to={`/${getCurrentCulture()}/quick-play/most-played-tickets`}
								className="popup--most__btn popup--most__btn--secondary btn btn--med btn--primary--inverted"
							>
								<span className="btn__label">
									{t("COMMON.SHOW_MORE")}
								</span>
							</Link>
						</div>
					)}
					<div className="popup__footer__inner popup__footer__inner--right">
						<QuickPlayPaginationButton
							direction="prev"
							onClick={() => {
								rootStore.quickPlayStore.mostPlayedTicketsStore.changePage(
									ChangePageButton.PREVIOUS
								);
								analyticsTracker.trackModalSwitcher(
									"most-played"
								);
							}}
							disabled={
								rootStore.quickPlayStore.loaderStore
									.isLoadingProcess || isPrevAndNextDisabled
							}
							label={t("COMMON.PREVIOUS_BUTTON")}
						/>

						<Button
							disabled={
								rootStore.quickPlayStore.loaderStore
									.isLoadingProcess
							}
							className={classNames(
								"btn--med btn--block popup--most__btn btn quote--button",
								{
									"is-active":
										rootStore.quickPlayStore.mostPlayedTicketsStore.isInBetSlip.has(
											rootStore.quickPlayStore
												.mostPlayedTicketsStore
												.selectedBetSlip.betSlipId
										),
									"btn--icon inc":
										rootStore.quickPlayStore
											.mostPlayedTicketsStore
											.selectedBetSlip.quotaIndicator > 0,
									"btn--icon desc":
										rootStore.quickPlayStore
											.mostPlayedTicketsStore
											.selectedBetSlip.quotaIndicator < 0,
								}
							)}
							onClick={() => {
								if (
									!rootStore.quickPlayStore
										.mostPlayedTicketsStore.selectedBetSlip
								) {
									return;
								}
								analyticsTracker.trackQuickPlayInteraction(
									MostPlayTicketActionsTypeEnum.TicketUse
								);
								if (
									!rootStore.quickPlayStore.mostPlayedTicketsStore.isInBetSlip.has(
										rootStore.quickPlayStore
											.mostPlayedTicketsStore
											.selectedBetSlip.betSlipId
									)
								) {
									analyticsTracker.trackUseTicketEvent(
										"most-played"
									);
								}
								rootStore.quickPlayStore.mostPlayedTicketsStore.addOrRemoveMostPlayedTicket(
									rootStore.quickPlayStore
										.mostPlayedTicketsStore.selectedBetSlip
								);
								rootStore.quickPlayStore.mostPlayedTicketsStore.deactivatePopup();
							}}
							type="button"
							iconClassName="u-icon u-icon--sml u-icon--refresh u-color--core--snow"
							btnText={rootStore.quickPlayStore.mostPlayedTicketsStore.selectedBetSlip.maxCoefficient.toFixed(
								2
							)}
							btnTextClassName="u-type--center"
						/>
						<QuickPlayPaginationButton
							direction="next"
							onClick={() => {
								rootStore.quickPlayStore.mostPlayedTicketsStore.changePage(
									ChangePageButton.NEXT
								);
								analyticsTracker.trackModalSwitcher(
									"most-played"
								);
							}}
							disabled={
								rootStore.quickPlayStore.loaderStore
									.isLoadingProcess || isPrevAndNextDisabled
							}
							label={t("COMMON.NEXT_BUTTON")}
						/>
					</div>
				</div>
			</div>
		</div>
	);
});
